// mri-viewer.js — NiiVue-based MRI viewer with server-side STL export.
//
// Loads preprocessed NIfTI volumes from the Harrison API, renders them in a
// single NiiVue canvas (axial / sagittal / coronal / 3D render tabs), exposes
// a settings panel for volume selection + render tuning, and ships the
// threshold to the server for marching-cubes STL generation.

import { Niivue, SLICE_TYPE } from '@niivue/niivue';

const API_BASE = window.API_BASE || '/api';

let nv = null;
let currentVolume = null;
let currentStats = null;

const state = {
  sliceType: 'render',
  colormap: 'viridis',
  threshold: 0,
  smoothingIterations: 8,
  decimationFaces: 200_000,
  largestComponentOnly: true,
};

const COLORMAPS = [
  { id: 'gray', label: 'Grayscale' },
  { id: 'plasma', label: 'Plasma' },
  { id: 'viridis', label: 'Viridis' },
  { id: 'inferno', label: 'Inferno' },
  { id: 'magma', label: 'Magma' },
  { id: 'turbo', label: 'Turbo' },
  { id: 'hot', label: 'Hot' },
  { id: 'bone', label: 'Bone' },
];

const SLICE_MODES = [
  { id: 'render', label: '3D Render', type: SLICE_TYPE.RENDER },
  { id: 'axial', label: 'Axial', type: SLICE_TYPE.AXIAL },
  { id: 'sagittal', label: 'Sagittal', type: SLICE_TYPE.SAGITTAL },
  { id: 'coronal', label: 'Coronal', type: SLICE_TYPE.CORONAL },
  { id: 'multi', label: 'Multi-plane', type: SLICE_TYPE.MULTIPLANAR },
];

function $(id) {
  return document.getElementById(id);
}

function setStatus(text, tone = 'info') {
  const el = $('status');
  if (!el) return;
  el.textContent = text;
  el.style.color = tone === 'error' ? '#ff6b6b' : tone === 'ok' ? '#6bff8a' : '';
}

async function fetchJSON(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ''}`);
  }
  return res.json();
}

function buildSettingsPanel(container) {
  const panel = document.createElement('div');
  panel.className = 'mri-settings';
  panel.innerHTML = `
    <button type="button" id="mri-settings-toggle" class="mri-settings__toggle">
      <i class="fa-solid fa-sliders"></i>
      <span>Settings</span>
    </button>
    <div id="mri-settings-content" class="mri-settings__content">
      <div class="mri-field">
        <label for="mri-volume-select">Volume</label>
        <select id="mri-volume-select"></select>
      </div>

      <div class="mri-field">
        <label for="mri-slice-select">View</label>
        <select id="mri-slice-select">
          ${SLICE_MODES.map((m) => `<option value="${m.id}">${m.label}</option>`).join('')}
        </select>
      </div>

      <div class="mri-field">
        <label for="mri-colormap-select">Colormap</label>
        <select id="mri-colormap-select">
          ${COLORMAPS.map((c) => `<option value="${c.id}">${c.label}</option>`).join('')}
        </select>
      </div>

      <div class="mri-field">
        <label>
          Clip plane
          <span class="mri-hint">drag on render with shift</span>
        </label>
        <div class="mri-row">
          <button type="button" id="mri-clip-reset" class="mri-btn">Reset</button>
        </div>
      </div>

      <hr>

      <div class="mri-field">
        <label for="mri-threshold">
          STL iso-value <span id="mri-threshold-val" class="mri-hint"></span>
        </label>
        <input type="range" id="mri-threshold" min="0" max="1" step="0.01" value="0.5">
        <div class="mri-row mri-row--between mri-hint">
          <span id="mri-threshold-min">—</span>
          <span id="mri-threshold-max">—</span>
        </div>
      </div>

      <div class="mri-field">
        <label for="mri-smoothing">
          Smoothing passes <span id="mri-smoothing-val" class="mri-hint"></span>
        </label>
        <input type="range" id="mri-smoothing" min="0" max="30" step="1" value="8">
      </div>

      <div class="mri-field">
        <label for="mri-decimation">
          Target faces <span id="mri-decimation-val" class="mri-hint"></span>
        </label>
        <input type="range" id="mri-decimation" min="0" max="1000000" step="10000" value="200000">
      </div>

      <div class="mri-field">
        <label class="mri-check">
          <input type="checkbox" id="mri-largest" checked>
          Largest component only
        </label>
      </div>

      <button type="button" id="mri-export-stl" class="mri-btn mri-btn--primary" disabled>
        <i class="fa-solid fa-download"></i>
        Export STL
      </button>
      <div id="mri-export-status" class="mri-hint"></div>
    </div>
  `;

  container.insertAdjacentElement('beforebegin', panel);

  $('mri-settings-toggle').addEventListener('click', () => {
    const content = $('mri-settings-content');
    content.classList.toggle('mri-settings__content--open');
  });

  $('mri-slice-select').addEventListener('change', (e) => {
    state.sliceType = e.target.value;
    applySliceType();
  });

  $('mri-colormap-select').addEventListener('change', (e) => {
    state.colormap = e.target.value;
    if (nv && nv.volumes && nv.volumes[0]) {
      nv.volumes[0].colormap = state.colormap;
      nv.updateGLVolume();
    }
  });

  $('mri-clip-reset').addEventListener('click', () => {
    if (nv) nv.setClipPlane([2, 0, 0]);
  });

  $('mri-threshold').addEventListener('input', (e) => {
    state.threshold = Number.parseFloat(e.target.value);
    $('mri-threshold-val').textContent = state.threshold.toFixed(1);
  });

  $('mri-smoothing').addEventListener('input', (e) => {
    state.smoothingIterations = Number.parseInt(e.target.value, 10);
    $('mri-smoothing-val').textContent = `${state.smoothingIterations}`;
  });

  $('mri-decimation').addEventListener('input', (e) => {
    state.decimationFaces = Number.parseInt(e.target.value, 10);
    $('mri-decimation-val').textContent = state.decimationFaces === 0
      ? 'no decimation'
      : `${(state.decimationFaces / 1000).toFixed(0)}k`;
  });

  $('mri-largest').addEventListener('change', (e) => {
    state.largestComponentOnly = e.target.checked;
  });

  $('mri-export-stl').addEventListener('click', exportSTL);

  $('mri-smoothing-val').textContent = `${state.smoothingIterations}`;
  $('mri-decimation-val').textContent = `${(state.decimationFaces / 1000).toFixed(0)}k`;
}

function applySliceType() {
  if (!nv) return;
  const mode = SLICE_MODES.find((m) => m.id === state.sliceType);
  if (!mode) return;
  nv.setSliceType(mode.type);
}

async function populateVolumes() {
  const select = $('mri-volume-select');
  select.innerHTML = '<option value="">Loading…</option>';
  try {
    const { volumes } = await fetchJSON('/mri/volumes');
    if (!volumes || volumes.length === 0) {
      select.innerHTML = '<option value="">No volumes — run dicom_to_nifti.py</option>';
      setStatus('No preprocessed volumes found. Run the DICOM→NIfTI script.', 'error');
      return;
    }
    select.innerHTML = volumes.map((v) => `<option value="${v}">${v}</option>`).join('');
    select.addEventListener('change', (e) => loadVolume(e.target.value));
    const preferred = volumes.includes('sag_t1_mprage') ? 'sag_t1_mprage' : volumes[0];
    select.value = preferred;
    await loadVolume(preferred);
  } catch (err) {
    console.error(err);
    select.innerHTML = '<option value="">Error loading volumes</option>';
    setStatus(`Failed to list volumes: ${err.message}`, 'error');
  }
}

async function loadVolume(name) {
  if (!name || !nv) return;

  setStatus(`Loading ${name}…`);
  $('mri-export-stl').disabled = true;

  try {
    const stats = await fetchJSON(`/mri/volumes/${encodeURIComponent(name)}/stats`);
    currentStats = stats;

    const volumeUrl = `${API_BASE}/mri/volumes/${encodeURIComponent(name)}.nii.gz`;
    await nv.loadVolumes([
      {
        url: volumeUrl,
        name: `${name}.nii.gz`,
        colormap: state.colormap,
        opacity: 1.0,
      },
    ]);

    currentVolume = name;

    // Seed the threshold slider from volume stats. p95 is a reasonable start
    // for "skin surface" on most MRI sequences; the user drags from there.
    const slider = $('mri-threshold');
    const min = stats.min;
    const max = stats.max;
    const seed = Math.max(stats.p50, Math.min(stats.p95, max));
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(Math.max((max - min) / 500, 0.001));
    slider.value = String(seed);
    state.threshold = seed;

    $('mri-threshold-val').textContent = seed.toFixed(1);
    $('mri-threshold-min').textContent = min.toFixed(0);
    $('mri-threshold-max').textContent = max.toFixed(0);
    $('mri-export-stl').disabled = false;

    applySliceType();
    setStatus(`Loaded ${name} (${stats.shape.join('×')} voxels)`, 'ok');
  } catch (err) {
    console.error(err);
    setStatus(`Failed to load ${name}: ${err.message}`, 'error');
  }
}

async function exportSTL() {
  if (!currentVolume) return;
  const btn = $('mri-export-stl');
  const statusEl = $('mri-export-status');
  btn.disabled = true;
  statusEl.textContent = 'Generating mesh (may take 10–60s)…';
  statusEl.style.color = '';

  const body = {
    threshold: state.threshold,
    smoothing_iterations: state.smoothingIterations,
    decimation_target_faces: state.decimationFaces,
    largest_component_only: state.largestComponentOnly,
  };

  try {
    const res = await fetch(
      `${API_BASE}/mri/volumes/${encodeURIComponent(currentVolume)}/stl`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ''}`);
    }
    const blob = await res.blob();
    const verts = res.headers.get('X-Mesh-Vertices');
    const faces = res.headers.get('X-Mesh-Faces');
    const elapsed = res.headers.get('X-Mesh-Elapsed-Seconds');

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentVolume}_iso${Math.round(state.threshold)}.stl`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    statusEl.textContent = `STL ready: ${faces} faces, ${verts} verts in ${elapsed}s`;
    statusEl.style.color = '#6bff8a';
  } catch (err) {
    console.error(err);
    statusEl.textContent = `Export failed: ${err.message}`;
    statusEl.style.color = '#ff6b6b';
  } finally {
    btn.disabled = false;
  }
}

async function init() {
  const viewerElement = $('viewer');
  if (!viewerElement) {
    console.error('MRI viewer container not found');
    return;
  }
  if (viewerElement.offsetParent === null) return;
  if (viewerElement.dataset.mriInitialized === '1') return;
  viewerElement.dataset.mriInitialized = '1';

  viewerElement.innerHTML = '';
  viewerElement.style.position = 'relative';
  viewerElement.style.width = '100%';
  viewerElement.style.height = '640px';
  viewerElement.style.background = '#000';

  const canvas = document.createElement('canvas');
  canvas.id = 'mri-canvas';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  viewerElement.appendChild(canvas);

  nv = new Niivue({
    show3Dcrosshair: false,
    backColor: [0, 0, 0, 1],
    isColorbar: false,
    isOrientCube: true,
    textHeight: 0.03,
  });

  await nv.attachToCanvas(canvas);
  nv.setSliceType(SLICE_TYPE.RENDER);
  nv.setRenderAzimuthElevation(110, 10);

  buildSettingsPanel(viewerElement);
  await populateVolumes();
}

window.initMRIViewer = init;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
